import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Chip } from '../app/ui';
import { STATUS_TONE, linkHref } from '../lib/org';
import type { OrgAgent, OrgChart, OrgDepartment } from '../lib/org';

function AgentCard({ a }: { a: OrgAgent }) {
  return (
    <article className={`card agent${a.status === 'off' ? ' agent-off' : ''}`}>
      <div className="row gap">
        <span className={`dot dot-${a.status}`} role="img" aria-label={a.status} />
        <strong className={`grow${a.kind === 'head' ? ' head' : ''}`}>{a.name}</strong>
        {a.lane ? <Chip>{a.lane}</Chip> : null}
      </div>
      {a.title ? <div className="muted small">{a.title}</div> : null}
      {a.currentTask ? <div className="small">{a.currentTask}</div> : a.status !== 'idle' ? <div className="muted small">{a.status}</div> : null}
      {a.links.length ? (
        <div className="row wrap gap">
          {a.links.map((l) => <Link key={`${l.kind}:${l.ref}`} className="chip chip-accent" to={linkHref(l)}>{l.ref}</Link>)}
        </div>
      ) : null}
    </article>
  );
}

function Tree({ a }: { a: OrgAgent }) {
  return (
    <div className="stack tight">
      <AgentCard a={a} />
      {a.children.length ? <div className="org-children">{a.children.map((c) => <Tree key={c.id} a={c} />)}</div> : null}
    </div>
  );
}

function Lane({ d }: { d: OrgDepartment }) {
  const [open, setOpen] = useState(true);
  return (
    <section className="org-lane">
      <button className="org-lane-head" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span>{open ? '▾' : '▸'}</span><h3 className="grow">{d.name}</h3>
        {d.activeCount ? <span className="muted small">{d.activeCount} active</span> : null}
        <span className="muted small">{d.agentCount}</span>
      </button>
      {open ? <div className="stack tight">{d.agents.map((a) => <Tree key={a.id} a={a} />)}</div> : null}
    </section>
  );
}

export function OrgView({ org }: { org: OrgChart | null }) {
  if (!org) return <p className="muted">The organisation chart is not available yet. It appears once the server has the org schema and seed (NEXUS-740).</p>;
  const s = org.summary;
  return (
    <div className="stack">
      <div className="row wrap gap muted small">
        <strong>{s.agents} agents</strong>
        {(['working', 'waiting', 'blocked', 'off'] as const).map((k) => (s[k] ? <Chip key={k} tone={STATUS_TONE[k]}>{s[k]} {k}</Chip> : null))}
      </div>
      <div className="org-grid">{org.departments.map((d) => <Lane key={d.id} d={d} />)}</div>
    </div>
  );
}
